import React from 'react'
import PropTypes from 'prop-types'
import Slider from 'react-slick'
import useSWR from 'swr'
import styled from 'styled-components'

import { APIURL } from '../../constants'
import services from '../../components/services'
import { BannersContainer } from '../theme/banner-ads/BannersContainer'
import { radial, Container, ProductH3 } from '../theme/default/base'

const MediaBlock = styled.div`
  background: ${(props) => radial(props.theme?.colors?.primary || '#022241')};
  padding: 50px 0;
`

const SliderWrapper = styled(Slider)`
  ${(props) => props.theme.media.phone`
    margin: 0 20px;
  `}

  .slick-track {
    flex: 1 0 auto;
    display: flex;
    flex-flow: nowrap;
    flex-direction: row;
    align-items: center;
    display: flex;
    justify-content: center;

    ${(props) => props.theme.media.phone`
      margin: 0 auto;
    `}

    & > div {
      flex: 1 1 auto;
      display: flex;
    }

    & > div > div {
      width: 100% !important;
    }

    .slick-slide {
      display: flex;
      flex-direction: column;
      flex: 1 0 auto;
      padding: 0 10px;
      height: 100%;
      justify-content: space-between;
      transition: all 0.25s;
      max-width: 350px;

      & > div {
        background-color: #fff;
        border-radius: 5px;
        padding: 20px;
      }

      &:hover {
        transform: scale(1.01);
        transition: all 0.25s;
      }

      img {
        flex: 1 0 auto;
        width: 100%;
      }
    }
  }

  &:hover {
    .slick-prev,
    .slick-next {
      background-color: rgba(#fff, 0.75);
    }
  }
`

const Header = styled(ProductH3)`
  text-align: center;
  color: #fff !important;
`

const VideoContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
  border-radius: 2px;

  ${(props) => props.theme.media.tablet`
    width: calc(90vw);
    max-width: calc(90vw);
    min-width: calc(90vw);
  `}
`

const Iframe = styled.iframe`
  width: 100%;
  height: 100%;
  padding: 0;
  margin: 0;
  border: 0 !important;
`

const Name = styled.div`
  margin-bottom: 20px;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
`

const P = styled.p`
  margin-top: 20px;
  text-align: center;
`

const A = styled.a`
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: ${(props) => props.theme.colors.black};
  flex: 1 0 auto;
  justify-content: space-between;
  text-decoration: none;

  &:hover {
    color: ${(props) => props.theme.colors.black};
    text-decoration: none;
  }
`

const CarouselItem = ({ title, displayTitle, url, imageUrl }) => {
  return (
    <div>
      <A href={url} target="_blank" el="noopener noreferrer">
        {displayTitle && <Name>{title}</Name>}
        <img alt="" src={imageUrl} />
      </A>
    </div>
  )
}

const MediaCarouselDisplay = (props) => {
  const thumbs = (props?.media?.length > 0 ? props.media : [])
    .sort((x, y) => Number(x.order) - Number(y.order))
    .map((m) => {
      if (m.type === 'video') {
        return (
          <VideoContainer key={m.id}>
            {m.display_title && <Name>{m.title}</Name>}
            <Iframe
              title="video"
              allowFullScreen={true}
              src={m.download_link || m.media_url}
            />
            <P>Click the video to play</P>
          </VideoContainer>
        )
      } else if (m.type === 'image') {
        return (
          <CarouselItem
            key={m.id}
            title={m.title}
            displayTitle={m.display_title}
            url={m.media_center.media}
            imageUrl={m?.media_center?.media_thumbnail}
          />
        )
      } else if (m.type === 'url') {
        return (
          <CarouselItem
            key={m.id}
            title={m.title}
            displayTitle={m.display_title}
            url={m.external_url}
            imageUrl={m?.media_center?.media_thumbnail}
          />
        )
      } else {
        return (
          <CarouselItem
            key={m.id}
            title={m.title}
            displayTitle={m.display_title}
            url={m.download_link || m.document_center.document}
            imageUrl={m?.media_center?.media_thumbnail}
          />
        )
      }
    })
  const settings = {
    dots: true,
    speed: 500,
    slidesToShow: Math.min(4, thumbs.length),
    slidesToScroll: 1,
    arrows: true,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: Math.min(4, thumbs.length)
        }
      },
      {
        breakpoint: 1000,
        settings: {
          slidesToShow: Math.min(2, thumbs.length)
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1
        }
      }
    ]
  }
  return (
    <div>
      <Header>Resources</Header>
      <SliderWrapper {...settings}>{thumbs}</SliderWrapper>
    </div>
  )
}

MediaCarouselDisplay.propTypes = {
  media: PropTypes.array
}

const MediaCarousel = ({ banners, product_id }) => {
  const { data, error } = useSWR(
    APIURL.API_MARKETPLACE_MEDIA(product_id),
    services.get_v2
  )

  if (error || !data) {
    return null
  }

  // No media for this one, so don't show it
  if (!data.response || data.response.length === 0) {
    return null
  }

  return (
    <MediaBlock id="media">
      <Container>
        <MediaCarouselDisplay media={data.response} />
        <BannersContainer banners={banners} placement="Product" />
      </Container>
    </MediaBlock>
  )
}

MediaCarousel.propTypes = {
  product_id: PropTypes.number,
  banners: PropTypes.array
}

export default MediaCarousel
